// /settings → COD tab → "Тооцоо" panel → detail + settle modal (§2.7a item 7).
// Renders: header, summary cards, per-DN breakdown, signatures, notes editor.
// Footer: SETTLE / DISPUTE / WRITE-OFF while status=DRAFT (BE settle.ts accepts DRAFT only; there is
// no SUBMITTED status, O101). The decisions need BIZ_GOV_MNG and stay visible without it (O102).

import {
  Box,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Image,
  Modal,
  NumberFormatter,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  Textarea,
  Title,
  rem,
} from "@mantine/core"
import {
  CheckCircleIcon,
  ProhibitIcon,
  WarningIcon,
} from "@phosphor-icons/react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import dayjs from "dayjs"
import type { ICodBreakdownLine, ICodReconciliation, SettleDecision } from "../../apis/cod-reconciliation"
import * as ReconApi from "../../apis/cod-reconciliation"
import { dateFormat } from "../../utils/date"
import { message } from "../../utils/message"
import { DeliveryActionButton } from "../delivery/action-controls"
import { DotPill } from "../delivery/atoms"
import { DeliveryAction, useDeliveryActions } from "../delivery/delivery-actions"
import { translateBackendError } from "../delivery/translate-error"
import { COD_STATUS_TONE, Money, useDriverNames } from "./cod-shared"
import { ConfirmNote, DisabledReason, HelpButtonGroup, nextStepToast } from "@dehub/theme"

/** @dehub/help keys per decision (action «?», confirm note). */
const DECISION_HELP: Record<SettleDecision, { action: string; confirm: string }> = {
  SETTLED: { action: "order.settings.action.cod-settle", confirm: "order.settings.confirm.cod-settle" },
  DISPUTED: { action: "order.settings.action.cod-dispute", confirm: "order.settings.confirm.cod-dispute" },
  WRITTEN_OFF: { action: "order.settings.action.cod-write-off", confirm: "order.settings.confirm.cod-write-off" },
}

const DECISION_ACTION: Record<SettleDecision, DeliveryAction> = {
  SETTLED: "codSettle",
  DISPUTED: "codDispute",
  WRITTEN_OFF: "codWriteOff",
}

// ── Confirmation dialog meta ─────────────────────────────────────────────────

type DecisionMeta = {
  decision: SettleDecision
  color: string
  haloVar: string
  haloAlpha: string
  borderVar: string
  borderAlpha: string
  icon: React.ReactNode
  labelKey: string
  titleKey: string
  summaryKey: string
}

const DECISION_META: DecisionMeta[] = [
  {
    decision: "SETTLED",
    color: "green",
    haloVar: "--mantine-color-green-6",
    haloAlpha: "8%",
    borderVar: "--mantine-color-green-3",
    borderAlpha: "100%",
    icon: <CheckCircleIcon size={56} />,
    labelKey: "cod.decisions.settle",
    titleKey: "cod.decisions.settleConfirm.title",
    summaryKey: "cod.decisions.settleConfirm.summary",
  },
  {
    decision: "DISPUTED",
    color: "orange",
    haloVar: "--mantine-color-orange-6",
    haloAlpha: "8%",
    borderVar: "--mantine-color-orange-3",
    borderAlpha: "100%",
    icon: <WarningIcon size={56} />,
    labelKey: "cod.decisions.dispute",
    titleKey: "cod.decisions.disputeConfirm.title",
    summaryKey: "cod.decisions.disputeConfirm.summary",
  },
  {
    decision: "WRITTEN_OFF",
    color: "red",
    haloVar: "--mantine-color-red-6",
    haloAlpha: "8%",
    borderVar: "--mantine-color-red-3",
    borderAlpha: "100%",
    icon: <ProhibitIcon size={56} />,
    labelKey: "cod.decisions.writeOff",
    titleKey: "cod.decisions.writeOffConfirm.title",
    summaryKey: "cod.decisions.writeOffConfirm.summary",
  },
]

// ── Summary sub-card (3-up grid) ─────────────────────────────────────────────

function AmountCard({ label, value }: { label: string; value: number }) {
  return (
    <Card withBorder radius="md" p="sm">
      <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)" mb={2}>
        {label}
      </Text>
      <Text fw={650} fz="var(--nw-fz-lead)" ff="monospace">
        <Money value={Number(value) || 0} />
      </Text>
    </Card>
  )
}

// ── Confirm sub-modal ────────────────────────────────────────────────────────

type ConfirmSubModalProps = {
  opened: boolean
  meta: DecisionMeta | null
  recon: ICodReconciliation | null
  notes: string
  loading: boolean
  onConfirm: () => void
  onCancel: () => void
}

function ConfirmSubModal({
  opened,
  meta,
  recon,
  notes,
  loading,
  onConfirm,
  onCancel,
}: ConfirmSubModalProps) {
  const { t } = useTranslation("settings")
  const driverName = useDriverNames()

  if (!meta || !recon) return null

  // CSS-variable color-mix halo — documented escape hatch per design-system-mantine.md
  const haloStyle: React.CSSProperties = {
    background: `color-mix(in srgb, var(${meta.haloVar}) ${meta.haloAlpha}, transparent)`,
    border: `1px solid var(${meta.borderVar})`,
  }

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title={t(meta.titleKey)}
      size="md"
      centered
    >
      <Stack gap="md">
        {/* Semantic icon with tinted halo */}
        <Center>
          <Box
            p="xl"
            style={{
              borderRadius: "var(--mantine-radius-xl)",
              ...haloStyle,
            }}
            c={meta.color}
          >
            {meta.icon}
          </Box>
        </Center>

        {/* Summary card — driver / workDate / amounts */}
        <Card withBorder radius="md" p="md" style={haloStyle}>
          <Stack gap="xs">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {t("cod.detail.signatures.driver")}
              </Text>
              <Text size="sm" fw={500}>
                {driverName(recon.driverUserId)}
              </Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {t("cod.reconciliation.table.workDate")}
              </Text>
              <Text size="sm" fw={500}>
                {dateFormat(recon.workDate)}
              </Text>
            </Group>
            <Divider />
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {t("cod.reconciliation.summary.expected")}
              </Text>
              <Text size="sm" fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>
                <NumberFormatter
                  value={recon.expectedAmount}
                  thousandSeparator=","
                  suffix="₮"
                />
              </Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {t("cod.reconciliation.summary.actual")}
              </Text>
              <Text size="sm" fw={600} style={{ fontVariantNumeric: "tabular-nums" }}>
                <NumberFormatter
                  value={recon.actualAmount}
                  thousandSeparator=","
                  suffix="₮"
                />
              </Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                {t("cod.reconciliation.summary.discrepancy")}
              </Text>
              <Text
                size="sm"
                fw={600}
                c={recon.discrepancyAmount === 0 ? "green" : "orange"}
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                <NumberFormatter
                  value={recon.discrepancyAmount}
                  thousandSeparator=","
                  suffix="₮"
                />
              </Text>
            </Group>
            {notes.trim() && (
              <>
                <Divider />
                <Group justify="space-between" align="flex-start">
                  <Text size="sm" c="dimmed">
                    {t("cod.detail.varianceReason.label")}
                  </Text>
                  <Text size="sm" maw={300} ta="right">
                    {notes}
                  </Text>
                </Group>
              </>
            )}
          </Stack>
        </Card>

        <Text size="sm" c="dimmed" ta="center">
          {t(meta.summaryKey)}
        </Text>
        <ConfirmNote helpKey={DECISION_HELP[meta.decision].confirm} mt={0} />

        <Group justify="space-between" mt="xs">
          <Button variant="default" onClick={onCancel} disabled={loading}>
            {t("common.cancel")}
          </Button>
          <Button color={meta.color} onClick={onConfirm} loading={loading}>
            {t(meta.labelKey)}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

// ── Per-DN breakdown table ───────────────────────────────────────────────────
// O103: stored lines are { deliveryNoteId, codAmount, codPaymentMethod, capturedAt } and GET adds
// deliveryNoteRefCode. Per-line expected/actual/discrepancy do not exist (totals live on the header).

function PerDnBreakdownTable({ rows }: { rows: ICodBreakdownLine[] }) {
  const { t } = useTranslation("delivery")
  if (rows.length === 0) return null
  return (
    <Stack gap="xs">
      <Title order={4} fz="var(--nw-fz-card)">
        {t("manage.cod.breakdownTitle")}
      </Title>
      <Table withTableBorder verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("manage.cod.colDn")}</Table.Th>
            <Table.Th>{t("manage.cod.colMethod")}</Table.Th>
            <Table.Th>{t("manage.cod.colCaptured")}</Table.Th>
            <Table.Th ta="right">{t("manage.cod.colAmount")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, i) => (
            <Table.Tr key={row.deliveryNoteId ?? i}>
              <Table.Td>
                <Text ff="monospace" fz="var(--nw-fz-mono)">
                  {row.deliveryNoteRefCode ?? "-"}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text fz="var(--nw-fz-body)">
                  {row.codPaymentMethod ? t(`manage.cod.method.${row.codPaymentMethod}`, { defaultValue: "-" }) : "-"}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text ff="monospace" fz="var(--nw-fz-mono)">
                  {row.capturedAt ? dayjs(row.capturedAt).format("YYYY-MM-DD HH:mm") : "-"}
                </Text>
              </Table.Td>
              <Table.Td ta="right">
                <Text ff="monospace" fz="var(--nw-fz-mono)">
                  {row.codAmount != null ? <Money value={Number(row.codAmount) || 0} /> : "-"}
                </Text>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  )
}

// ── Main modal ───────────────────────────────────────────────────────────────

type Props = {
  opened: boolean
  reconciliationId: string | null
  onClose: () => void
  /** Called after a successful settle action — parent should mutate SWR + close. */
  onActioned: () => void
}

export function CodReconciliationDetailModal({
  opened,
  reconciliationId,
  onClose,
  onActioned,
}: Props) {
  const { t } = useTranslation(["settings", "delivery"])
  const { stateOf } = useDeliveryActions()
  const driverName = useDriverNames()

  const { data, isLoading } = useSWR<ICodReconciliation>(
    reconciliationId ? ["cod-reconciliation.get", reconciliationId] : null,
    () => ReconApi.get(reconciliationId!),
    { revalidateOnFocus: false },
  )

  const [notes, setNotes] = useState("")
  const [notesError, setNotesError] = useState<string | null>(null)
  const [confirmTarget, setConfirmTarget] = useState<SettleDecision | null>(null)
  const [settling, setSettling] = useState(false)

  // Sync notes from server data whenever it loads / changes
  useEffect(() => {
    if (data) {
      setNotes(data.notes ?? "")
    }
  }, [data])

  const handleSettle = async () => {
    if (!reconciliationId || !confirmTarget) return
    if (confirmTarget === "WRITTEN_OFF" && !notes.trim()) {
      setNotesError(t("cod.detail.varianceReason.required"))
      return
    }
    setSettling(true)
    try {
      await ReconApi.settle(reconciliationId, {
        decision: confirmTarget,
        notes: notes.trim() || null,
      })
      setConfirmTarget(null)
      setNotesError(null)
      nextStepToast("order.settings.toast.cod-decided")
      onActioned()
    } catch (e) {
      message.error(translateBackendError(e))
    } finally {
      setSettling(false)
    }
  }

  const confirmMeta =
    confirmTarget != null
      ? DECISION_META.find((m) => m.decision === confirmTarget) ?? null
      : null

  // O101: decisions apply while DRAFT (stateOf also carries the BIZ_GOV_MNG reason, O102).
  const isDraft = stateOf("codSettle", data).applies
  const decisionStates = (["DISPUTED", "WRITTEN_OFF", "SETTLED"] as SettleDecision[]).map((decision) => ({
    decision,
    state: stateOf(DECISION_ACTION[decision], data),
  }))
  const deniedReason = decisionStates.find((d) => !d.state.allowed)?.state.reason ?? null

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={t("cod.reconciliation.title")}
        size="xl"
      >
        {isLoading || !data ? (
          <Stack gap="sm">
            <Skeleton height={32} radius="md" />
            <Skeleton height={80} radius="md" />
            <Skeleton height={200} radius="md" />
          </Stack>
        ) : (
          <Stack gap="md">
            {/* ── Header ──────────────────────────────────────────────── */}
            <Group gap="sm" align="center">
              <Stack gap={2} style={{ flex: 1 }}>
                <Group gap="sm">
                  <Text fw={650} fz="var(--nw-fz-card)" ff="monospace">
                    {dateFormat(data.workDate)}
                  </Text>
                  <DotPill tone={COD_STATUS_TONE[data.status] ?? "grey"}>
                    {t(`cod.reconciliation.status.${data.status}`, { defaultValue: "-" })}
                  </DotPill>
                </Group>
                <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                  {t("cod.reconciliation.table.driver")}: {driverName(data.driverUserId)}
                </Text>
                {data.deliveryCount != null && (
                  <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                    {t("cod.reconciliation.table.deliveryCount")}: {data.deliveryCount}
                  </Text>
                )}
              </Stack>
            </Group>

            {/* ── Amount summary ───────────────────────────────────────── */}
            <SimpleGrid cols={3}>
              <AmountCard label={t("cod.reconciliation.summary.expected")} value={data.expectedAmount} />
              <AmountCard label={t("cod.reconciliation.summary.actual")} value={data.actualAmount} />
              <AmountCard label={t("cod.reconciliation.summary.discrepancy")} value={data.discrepancyAmount} />
            </SimpleGrid>

            {/* ── Per-DN breakdown ─────────────────────────────────────── */}
            {data.perDnBreakdown && data.perDnBreakdown.length > 0 && <PerDnBreakdownTable rows={data.perDnBreakdown} />}

            {/* ── Signatures ──────────────────────────────────────────── */}
            {(data.driverSignatureUrl || data.cashierSignatureUrl) && (
              <Stack gap="xs">
                <Title order={6}>{t("cod.detail.signaturesSection")}</Title>
                <Group gap="md" align="flex-start">
                  {data.driverSignatureUrl && (
                    <Stack gap={4}>
                      <Text size="sm" c="dimmed" fw={500} tt="uppercase">
                        {t("cod.detail.signatures.driver")}
                      </Text>
                      <Image
                        src={data.driverSignatureUrl}
                        radius="sm"
                        h={100}
                        fit="contain"
                        maw={rem(240)}
                        style={{
                          background: "var(--mantine-color-gray-0)",
                        }}
                        alt={t("cod.detail.signatures.driver")}
                      />
                    </Stack>
                  )}
                  {data.cashierSignatureUrl && (
                    <Stack gap={4}>
                      <Text size="sm" c="dimmed" fw={500} tt="uppercase">
                        {t("cod.detail.signatures.cashier")}
                      </Text>
                      <Image
                        src={data.cashierSignatureUrl}
                        radius="sm"
                        h={100}
                        fit="contain"
                        maw={rem(240)}
                        style={{
                          background: "var(--mantine-color-gray-0)",
                        }}
                        alt={t("cod.detail.signatures.cashier")}
                      />
                    </Stack>
                  )}
                </Group>
              </Stack>
            )}

            {/* ── Custody transfer ref ─────────────────────────────────── */}
            {data.custodyTransferId && (
              <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                {t("delivery:manage.cod.custodyLinked")}
              </Text>
            )}

            {/* ── Settled / reconciled meta ────────────────────────────── */}
            {(data.settledAt || data.reconciledAt) && (
              <Group gap="xl">
                {data.reconciledAt && (
                  <Stack gap={2}>
                    <Text size="sm" c="dimmed">
                      {t("cod.detail.reconciledAt")}
                    </Text>
                    <Text ff="monospace" fz="var(--nw-fz-mono)">
                      {dayjs(data.reconciledAt).format("YYYY-MM-DD HH:mm")}
                    </Text>
                  </Stack>
                )}
                {data.settledAt && (
                  <Stack gap={2}>
                    <Text size="sm" c="dimmed">
                      {t("cod.detail.settledAt")}
                    </Text>
                    <Text ff="monospace" fz="var(--nw-fz-mono)">
                      {dayjs(data.settledAt).format("YYYY-MM-DD HH:mm")}
                    </Text>
                  </Stack>
                )}
              </Group>
            )}

            {/* ── Notes ───────────────────────────────────────────────── */}
            <Textarea
              label={t("cod.detail.varianceReason.label")}
              placeholder={isDraft ? t("cod.detail.varianceReason.label") + "..." : undefined}
              value={notes}
              onChange={(e) => {
                setNotes(e.currentTarget.value)
                if (notesError) setNotesError(null)
              }}
              error={notesError}
              readOnly={!isDraft}
              autosize
              minRows={2}
              maxRows={5}
            />

            <Divider />

            {/* ── Footer actions ───────────────────────────────────────── */}
            <Group justify="space-between">
              <Button variant="default" onClick={onClose}>
                {t("common.close")}
              </Button>
              {isDraft && (
                <Stack gap={4} align="flex-end">
                  <Group gap="xs">
                    {decisionStates.map(({ decision, state }) => (
                      // @dehub/help: attached «?» (R2) + reason popover while unavailable (R4).
                      <DisabledReason
                        key={decision}
                        helpKey="order.settings.reason.cod-decision"
                        reason={/эрх|permission/i.test(state.reason ?? "") ? "PERM" : "NOT_DRAFT"}
                        when={!state.allowed}
                        position="bottom-end"
                      >
                        <HelpButtonGroup helpKey={DECISION_HELP[decision].action}>
                          <DeliveryActionButton
                            state={state}
                            variant={decision === "SETTLED" ? "filled" : "default"}
                            color={decision === "SETTLED" ? "dark" : undefined}
                            onClick={() => setConfirmTarget(decision)}
                          >
                            {t(DECISION_META.find((m) => m.decision === decision)!.labelKey)}
                          </DeliveryActionButton>
                        </HelpButtonGroup>
                      </DisabledReason>
                    ))}
                  </Group>
                  {deniedReason && (
                    <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                      {deniedReason}
                    </Text>
                  )}
                </Stack>
              )}
            </Group>
          </Stack>
        )}
      </Modal>

      {/* ── Confirmation sub-modal ───────────────────────────────────────── */}
      <ConfirmSubModal
        opened={Boolean(confirmTarget)}
        meta={confirmMeta}
        recon={data ?? null}
        notes={notes}
        loading={settling}
        onConfirm={handleSettle}
        onCancel={() => setConfirmTarget(null)}
      />
    </>
  )
}
